export type AdminNotificationType =
  | 'support_ticket_created'
  | 'support_ticket_message'
  | 'contact_form_submitted'
  | 'user_feedback_submitted'
  | 'cron_failed'
  | 'webhook_failed'
  | 'entitlement_mismatch'
  | 'refund_spike'
  | 'entitlement_store_sample'
  | 'community_repeat_offender'
  | 'purchase_verify_mismatch'
  | 'shared_apple_subscription'
  | 'shared_google_subscription'

export type AdminNotificationSeverity = 'info' | 'warning' | 'critical'

export type AdminNotificationEntityKind =
  | 'supportTicket'
  | 'feedback'
  | 'cron'
  | 'webhook'
  | 'subscription'
  | 'user'

export interface AdminNotificationEntity {
  kind?: AdminNotificationEntityKind
  id?: string
}

export interface AdminNotificationActor {
  userId?: string
  name?: string
  email?: string
}

export interface AdminNotificationMeta {
  supportTicketID?: string
  category?: string
  subject?: string
  subscription?: string
  job?: string
  provider?: string
  operation?: string
  errorMessage?: string
  failedBatches?: number
  reason?: string
  notificationType?: string
  notificationSubtype?: string
  notificationUUID?: string
  environment?: string
  bundleId?: string
  productId?: string
  originalTransactionId?: string
  transactionId?: string
  userId?: string
  /** Sanitised leftover keys as a JSON string. Never includes signedPayload. */
  payloadSummary?: string
  /** Growth / billing meta */
  platform?: string
  mode?: string
  source?: string
  purchaseToken?: string
  winnerUserId?: string
  loserUserIds?: string[]
  rowCount?: number
  sampled?: number
  ok?: number
  storeInactiveBackendLive?: number
  storeActiveBackendStale?: number
  errors?: number
  skippedNoCreds?: number
  mismatches?: number
  affectedUsers?: number
  refundCount?: number
  threshold?: number
  windowHours?: number
}

export interface AdminNotification {
  _id: string
  type: AdminNotificationType
  severity: AdminNotificationSeverity
  title: string
  body?: string
  count: number
  isRead: boolean
  lastEventAt: string
  createdAt: string
  entity?: AdminNotificationEntity | null
  actor?: AdminNotificationActor | null
  meta?: AdminNotificationMeta | null
}

// Socket payloads are broadcast to every admin, so they never carry isRead.
export type AdminNotificationSocketPayload = Omit<AdminNotification, 'isRead'>

export interface AdminNotificationListResponse {
  status: number
  message: string
  unreadCount: number
  totalDocs: number
  page: number
  totalPages: number
  hasNextPage: boolean
  data: AdminNotification[]
}

export interface AdminNotificationTypeCount {
  type: AdminNotificationType
  count: number
}

export interface AdminNotificationUnreadCountResponse {
  total: number
  countsByType: AdminNotificationTypeCount[]
}

export interface MarkAdminNotificationsReadResponse {
  updated: number
  unreadTotal: number
}

export interface AdminNotificationFilters {
  page?: number
  pageSize?: number
  types?: AdminNotificationType[]
  unreadOnly?: boolean
  startDate?: string
  endDate?: string
}

/** Growth / billing types for Failures page secondary filters */
export const GROWTH_ALERT_TYPES: AdminNotificationType[] = [
  'entitlement_mismatch',
  'refund_spike',
  'entitlement_store_sample',
  'community_repeat_offender',
  'purchase_verify_mismatch',
  'shared_apple_subscription',
  'shared_google_subscription',
]

export const BILLING_ALERT_TYPES: AdminNotificationType[] = [
  'purchase_verify_mismatch',
  'shared_apple_subscription',
  'shared_google_subscription',
]

export const ENTITLEMENT_ALERT_TYPES: AdminNotificationType[] = [
  'entitlement_mismatch',
  'refund_spike',
  'entitlement_store_sample',
]
